/**
 * EditorToolbar Component
 *
 * Toolbar for markdown formatting actions and variable insertion.
 */
import { useState } from 'react';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link2,
  Table,
  Minus,
  Eye,
  EyeOff,
  Columns,
  Variable,
  Sparkles,
  ChevronDown,
  Undo2,
  Redo2,
} from 'lucide-react';
import { useEditorStore } from '../../stores/editorStore';
import { TEMPLATE_VARIABLES, TemplateVariable, SECTION_TYPES } from '../../types/editor';

interface EditorToolbarProps {
  onInsertText: (text: string) => void;
  onWrapSelection: (before: string, after: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onAIAction?: (action: string, sectionType?: string) => void;
  isAIEnabled?: boolean;
  isAILoading?: boolean;
}

export default function EditorToolbar({
  onInsertText,
  onWrapSelection,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onAIAction,
  isAIEnabled = false,
  isAILoading = false,
}: EditorToolbarProps) {
  // Use selectors to prevent unnecessary re-renders
  const previewMode = useEditorStore((state) => state.previewMode);
  const setPreviewMode = useEditorStore((state) => state.setPreviewMode);
  const [showVariables, setShowVariables] = useState(false);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);

  // Group variables by category
  const variablesByCategory = TEMPLATE_VARIABLES.reduce((acc, variable) => {
    if (!acc[variable.category]) {
      acc[variable.category] = [];
    }
    acc[variable.category].push(variable);
    return acc;
  }, {} as Record<string, TemplateVariable[]>);

  const categoryLabels: Record<string, string> = {
    cliente: 'Cliente',
    offerta: 'Offerta',
    tenant: 'Azienda',
    blocchi: 'Blocchi Speciali',
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-200">
      {/* Undo/Redo */}
      <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
        <ToolbarButton
          icon={<Undo2 className="h-4 w-4" />}
          onClick={onUndo}
          disabled={!canUndo}
          title="Annulla (Ctrl+Z)"
        />
        <ToolbarButton
          icon={<Redo2 className="h-4 w-4" />}
          onClick={onRedo}
          disabled={!canRedo}
          title="Ripristina (Ctrl+Y)"
        />
      </div>

      {/* Text formatting */}
      <ToolbarButton
        icon={<Bold className="h-4 w-4" />}
        onClick={() => onWrapSelection('**', '**')}
        title="Grassetto (Ctrl+B)"
      />
      <ToolbarButton
        icon={<Italic className="h-4 w-4" />}
        onClick={() => onWrapSelection('*', '*')}
        title="Corsivo (Ctrl+I)"
      />

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Headings dropdown */}
      <div className="relative">
        <ToolbarButton
          icon={
            <div className="flex items-center gap-1">
              <Heading1 className="h-4 w-4" />
              <ChevronDown className="h-3 w-3" />
            </div>
          }
          onClick={() => setShowHeadingMenu(!showHeadingMenu)}
          title="Titoli"
          active={showHeadingMenu}
        />
        {showHeadingMenu && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[120px]">
            <button
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              onClick={() => {
                onInsertText('\n# ');
                setShowHeadingMenu(false);
              }}
            >
              <Heading1 className="h-4 w-4" /> Titolo 1
            </button>
            <button
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              onClick={() => {
                onInsertText('\n## ');
                setShowHeadingMenu(false);
              }}
            >
              <Heading2 className="h-4 w-4" /> Titolo 2
            </button>
            <button
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              onClick={() => {
                onInsertText('\n### ');
                setShowHeadingMenu(false);
              }}
            >
              <Heading3 className="h-4 w-4" /> Titolo 3
            </button>
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Lists */}
      <ToolbarButton
        icon={<List className="h-4 w-4" />}
        onClick={() => onInsertText('\n- ')}
        title="Elenco puntato"
      />
      <ToolbarButton
        icon={<ListOrdered className="h-4 w-4" />}
        onClick={() => onInsertText('\n1. ')}
        title="Elenco numerato"
      />

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Block elements */}
      <ToolbarButton
        icon={<Quote className="h-4 w-4" />}
        onClick={() => onInsertText('\n> ')}
        title="Citazione"
      />
      <ToolbarButton
        icon={<Code className="h-4 w-4" />}
        onClick={() => onWrapSelection('`', '`')}
        title="Codice inline"
      />
      <ToolbarButton
        icon={<Link2 className="h-4 w-4" />}
        onClick={() => onWrapSelection('[', '](url)')}
        title="Link"
      />
      <ToolbarButton
        icon={<Minus className="h-4 w-4" />}
        onClick={() => onInsertText('\n---\n')}
        title="Linea orizzontale"
      />
      <ToolbarButton
        icon={<Table className="h-4 w-4" />}
        onClick={() => onInsertText('\n| Colonna 1 | Colonna 2 |\n| --- | --- |\n| Cella 1 | Cella 2 |\n')}
        title="Tabella"
      />

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Variables dropdown */}
      <div className="relative">
        <ToolbarButton
          icon={
            <div className="flex items-center gap-1">
              <Variable className="h-4 w-4" />
              <span className="text-xs">Variabili</span>
              <ChevronDown className="h-3 w-3" />
            </div>
          }
          onClick={() => setShowVariables(!showVariables)}
          title="Inserisci variabile"
          active={showVariables}
        />
        {showVariables && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-2 min-w-[280px] max-h-[400px] overflow-auto">
            {Object.entries(variablesByCategory).map(([category, variables]) => (
              <div key={category} className="mb-2">
                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
                  {categoryLabels[category]}
                </div>
                {variables.map((variable) => (
                  <button
                    key={variable.key}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center justify-between"
                    onClick={() => {
                      onInsertText(variable.key);
                      setShowVariables(false);
                    }}
                  >
                    <span>{variable.label}</span>
                    <code className="text-xs bg-gray-100 px-1 rounded">{variable.key}</code>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Menu */}
      {isAIEnabled && (
        <>
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <div className="relative">
            <ToolbarButton
              icon={
                <div className="flex items-center gap-1">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <span className="text-xs text-purple-600">AI</span>
                  <ChevronDown className="h-3 w-3" />
                </div>
              }
              onClick={() => setShowAIMenu(!showAIMenu)}
              title="Assistente AI"
              active={showAIMenu}
              disabled={isAILoading}
            />
            {showAIMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-2 min-w-[220px]">
                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
                  Genera Sezione
                </div>
                {SECTION_TYPES.map((section) => (
                  <button
                    key={section.id}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-purple-50 flex flex-col"
                    onClick={() => {
                      onAIAction?.('generate', section.id);
                      setShowAIMenu(false);
                    }}
                    disabled={isAILoading}
                  >
                    <span className="font-medium">{section.label}</span>
                    <span className="text-xs text-gray-500">{section.description}</span>
                  </button>
                ))}

                <div className="border-t border-gray-200 my-2" />

                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
                  Migliora Testo
                </div>
                <button
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-purple-50"
                  onClick={() => {
                    onAIAction?.('improve', 'general');
                    setShowAIMenu(false);
                  }}
                  disabled={isAILoading}
                >
                  Migliora selezionato
                </button>
                <button
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-purple-50"
                  onClick={() => {
                    onAIAction?.('improve', 'clarity');
                    setShowAIMenu(false);
                  }}
                  disabled={isAILoading}
                >
                  Rendi più chiaro
                </button>
                <button
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-purple-50"
                  onClick={() => {
                    onAIAction?.('improve', 'persuasion');
                    setShowAIMenu(false);
                  }}
                  disabled={isAILoading}
                >
                  Rendi più persuasivo
                </button>
                <button
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-purple-50"
                  onClick={() => {
                    onAIAction?.('expand', 'medium');
                    setShowAIMenu(false);
                  }}
                  disabled={isAILoading}
                >
                  Espandi testo
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* View mode toggles */}
      <div className="flex items-center border-l border-gray-300 pl-2">
        <ToolbarButton
          icon={<Columns className="h-4 w-4" />}
          onClick={() => setPreviewMode('split')}
          active={previewMode === 'split'}
          title="Vista divisa"
        />
        <ToolbarButton
          icon={<Eye className="h-4 w-4" />}
          onClick={() => setPreviewMode('preview')}
          active={previewMode === 'preview'}
          title="Solo anteprima"
        />
        <ToolbarButton
          icon={<EyeOff className="h-4 w-4" />}
          onClick={() => setPreviewMode('edit')}
          active={previewMode === 'edit'}
          title="Solo editor"
        />
      </div>
    </div>
  );
}

// Toolbar button component
interface ToolbarButtonProps {
  icon: React.ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  active?: boolean;
}

function ToolbarButton({ icon, onClick, title, disabled = false, active = false }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        p-1.5 rounded transition-colors
        ${active ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {icon}
    </button>
  );
}
